import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  HiOutlineMap,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlinePencil,
  HiOutlineSave,
  HiOutlineEye,
  HiOutlineGlobe,
  HiOutlineLocationMarker,
  HiOutlineX,
  HiOutlineChevronLeft,
  HiOutlineHome,
  HiOutlineSearch,
  HiOutlineCog,
  HiOutlineDocumentDuplicate,
  HiOutlineMenuAlt2,
  HiOutlineChevronDown,
  HiOutlineCheck,
  HiOutlineCursorClick,
} from 'react-icons/hi'
import clsx from 'clsx'
import toast, { Toaster } from 'react-hot-toast'
import mapService from '../services/mapService'
import AdvancedMapEditor from '../components/map/AdvancedMapEditor'
import MarkerTemplates from '../components/map/MarkerTemplates'
import DistanceCalculator from '../components/map/DistanceCalculator'
// Mobile components
import {
  MobileMapBottomSheet,
  MobileMarkerModal,
  MobileMapFAB,
  MobileLocationConfirm,
} from '../components/map/mobile'

// Default center: Da Nang
const DEFAULT_CENTER = { lat: 16.0544, lng: 108.2022 }
const DEFAULT_ZOOM = 13

// Map style options - giống với MarketMapEditor của admin
const MAP_STYLE_OPTIONS = [
  { value: 'roadmap', label: 'Đường phố', icon: '🛣️' },
  { value: 'satellite', label: 'Vệ tinh', icon: '🛰️' },
  { value: 'hybrid', label: 'Kết hợp', icon: '🗺️' },
  { value: 'terrain', label: 'Địa hình', icon: '⛰️' },
]

function MapEditor() {
  const { mapId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = !!mapId

  // Get auto-generated name from navigation state
  const autoName = location.state?.autoName || ''

  // Map state - use auto-generated name if creating new map
  const [mapData, setMapData] = useState({
    name: autoName,
    description: '',
    center_lat: DEFAULT_CENTER.lat,
    center_lng: DEFAULT_CENTER.lng,
    zoom_level: DEFAULT_ZOOM,
    thumbnail_url: '',
    map_style: 'roadmap',
    show_price: true,
    show_project_name: true,
    default_filter: 'all',
  })
  const [markers, setMarkers] = useState([])
  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)

  // UI state
  const [selectedMarker, setSelectedMarker] = useState(null)
  const [showMarkerForm, setShowMarkerForm] = useState(false)
  const [isAddingMarker, setIsAddingMarker] = useState(false)
  const [filterType, setFilterType] = useState('all')
  const [showSettings, setShowSettings] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [showTemplates, setShowTemplates] = useState(true)
  const [showDistanceCalc, setShowDistanceCalc] = useState(false)
  const [distanceLine, setDistanceLine] = useState(null)
  const [showAddMarkerMode, setShowAddMarkerMode] = useState(false) // Modal chọn cách thêm marker
  const [manualCoords, setManualCoords] = useState({ lat: '', lng: '' }) // Nhập tọa độ thủ công

  // Mobile state
  const [isMobile, setIsMobile] = useState(false)
  const [mobileAddingMarker, setMobileAddingMarker] = useState(false)
  const [showMobileMarkerModal, setShowMobileMarkerModal] = useState(false)
  const [pendingLocation, setPendingLocation] = useState(null) // Vị trí đang chờ xác nhận

  // New map creation flow state
  const [showCreateMapModal, setShowCreateMapModal] = useState(false)
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [tempMapName, setTempMapName] = useState('')
  const [createdMapId, setCreatedMapId] = useState(null) // ID của map vừa tạo

  // Map refs
  const mapEditorRef = useRef(null)
  const searchInputRef = useRef(null)

  // Marker form state
  const [markerForm, setMarkerForm] = useState({
    name: '',
    address: '',
    marker_type: 'custom',
    property_type: '',
    price_text: '',
    price_value: null,
    rent_price_text: '',
    rent_price_value: null,
    image_url: '',
    description: '',
    area_text: '',
    bedrooms: null,
    external_url: '',
    latitude: null,
    longitude: null,
    marker_color: '#3B82F6',
    label_text: '',
  })

  // Marker type options
  const markerTypeOptions = [
    { value: 'custom', label: 'Tự do', color: '#10B981', icon: '🏷️' },
    { value: 'ban', label: 'Bán', color: '#EF4444', icon: '💰' },
    { value: 'thue', label: 'Thuê', color: '#3B82F6', icon: '🏠' },
    { value: 'both', label: 'Cả hai', color: '#8B5CF6', icon: '🔄' },
  ]

  const propertyTypeOptions = mapService.getPropertyTypeOptions()

  const colorPresets = [
    { name: 'Xanh dương', value: '#3B82F6' },
    { name: 'Đỏ', value: '#EF4444' },
    { name: 'Xanh lá', value: '#10B981' },
    { name: 'Tím', value: '#8B5CF6' },
    { name: 'Cam', value: '#F59E0B' },
    { name: 'Hồng', value: '#EC4899' },
    { name: 'Xám', value: '#6B7280' },
  ]

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Show create map modal when creating new map
  useEffect(() => {
    if (!isEditMode && !createdMapId) {
      // Generate default name if not provided
      const defaultName = autoName || `Map ${new Date().toLocaleDateString('vi-VN')}`
      setTempMapName(defaultName)
      setMapData(prev => ({ ...prev, name: defaultName }))
      setShowCreateMapModal(true)
    }
  }, [isEditMode, autoName, createdMapId])

  // Load map data if editing
  useEffect(() => {
    if (isEditMode) {
      loadMapData()
    }
  }, [mapId])

  // Handle center change from AdvancedMapEditor (center marker drag)
  const handleCenterChange = useCallback((center) => {
    setMapData((prev) => ({
      ...prev,
      center_lat: center.lat,
      center_lng: center.lng,
    }))
  }, [])

  // Handle map move end - update pending location for mobile
  const handleMapMoveEnd = useCallback((center) => {
    if (mobileAddingMarker) {
      setPendingLocation({ lat: center.lat, lng: center.lng })
    }
  }, [mobileAddingMarker])

  // Handle zoom change from AdvancedMapEditor
  const handleZoomChange = useCallback((zoom) => {
    setMapData((prev) => ({
      ...prev,
      zoom_level: zoom,
    }))
  }, [])

  // Handle marker click for editing
  const handleMarkerClick = useCallback((marker) => {
    setSelectedMarker(marker)
    // Pan to marker if needed
    if (mapEditorRef.current) {
      mapEditorRef.current.panTo(marker.latitude, marker.longitude, 16)
    }
  }, [])

  // Handle temp marker confirm (when user clicks on map to add marker)
  const handleTempMarkerConfirm = useCallback(async (lat, lng) => {
    if (!isAddingMarker) return

    // Use Nominatim for reverse geocoding (free, no API key needed)
    let address = ''
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { 'Accept-Language': 'vi' } }
      )
      const data = await response.json()
      if (data.display_name) {
        address = data.display_name
      }
    } catch (e) {
      console.error('Reverse geocoding failed:', e)
    }

    setMarkerForm((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      address: address,
    }))
    setShowMarkerForm(true)
    setIsAddingMarker(false)
  }, [isAddingMarker])

  // Get marker color based on type
  const getMarkerColor = useCallback((marker) => {
    if (marker.marker_color) return marker.marker_color
    switch (marker.marker_type) {
      case 'ban': return '#EF4444'
      case 'thue': return '#3B82F6'
      case 'both': return '#8B5CF6'
      case 'custom': return '#10B981'
      default: return '#6B7280'
    }
  }, [])

  const loadMapData = async () => {
    setLoading(true)
    try {
      const [mapRes, markersRes] = await Promise.all([
        mapService.getMap(mapId),
        mapService.getMarkers(mapId),
      ])

      if (mapRes.status === 'success') {
        setMapData(mapRes.data)
      }
      if (markersRes.status === 'success') {
        setMarkers(markersRes.data || [])
      }
    } catch (error) {
      console.error('Failed to load map:', error)
      toast.error('Không thể tải thông tin map')
      navigate('/maps')
    } finally {
      setLoading(false)
    }
  }


  // Handle create map confirmation (from popup)
  const handleConfirmCreateMap = async (name) => {
    const mapName = name || tempMapName
    if (!mapName.trim()) {
      toast.error('Vui lòng nhập tên map')
      return
    }

    setSaving(true)
    try {
      const newMapData = { ...mapData, name: mapName }
      const response = await mapService.createMap(newMapData)

      if (response.status === 'success') {
        toast.success('Đã tạo map mới')
        setCreatedMapId(response.data.id)
        setShowCreateMapModal(false)
        setShowRenameModal(false)
        // Navigate to edit mode with the new map ID
        navigate(`/map/edit/${response.data.id}`, { replace: true })
      }
    } catch (error) {
      console.error('Failed to create map:', error)
      toast.error('Không thể tạo map')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveMap = async () => {
    if (!mapData.name.trim()) {
      toast.error('Vui lòng nhập tên map')
      return
    }

    setSaving(true)
    try {
      let response
      if (isEditMode || createdMapId) {
        const currentMapId = mapId || createdMapId
        response = await mapService.updateMap(currentMapId, mapData)
      } else {
        response = await mapService.createMap(mapData)
      }

      if (response.status === 'success') {
        toast.success(isEditMode || createdMapId ? 'Đã cập nhật map' : 'Đã tạo map mới')
        if (!isEditMode && !createdMapId) {
          navigate(`/map/edit/${response.data.id}`, { replace: true })
        }
      }
    } catch (error) {
      console.error('Failed to save map:', error)
      toast.error('Không thể lưu map')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveMarker = async () => {
    if (!markerForm.name.trim()) {
      toast.error('Vui lòng nhập tên marker')
      return
    }
    if (!markerForm.latitude || !markerForm.longitude) {
      toast.error('Vui lòng chọn vị trí trên bản đồ')
      return
    }

    try {
      let response
      if (markerForm.id) {
        response = await mapService.updateMarker(mapId, markerForm.id, markerForm)
      } else {
        response = await mapService.addMarker(mapId, markerForm)
      }

      if (response.status === 'success') {
        toast.success(markerForm.id ? 'Đã cập nhật marker' : 'Đã thêm marker', { id: 'save-marker' })

        if (markerForm.id) {
          setMarkers((prev) =>
            prev.map((m) => (m.id === markerForm.id ? response.data : m))
          )
        } else {
          setMarkers((prev) => [...prev, response.data])
        }

        resetMarkerForm()
      }
    } catch (error) {
      console.error('Failed to save marker:', error)
      toast.error('Không thể lưu marker', { id: 'save-marker-error' })
    }
  }

  const handleEditMarker = (marker) => {
    setMarkerForm({
      id: marker.id,
      name: marker.name || '',
      address: marker.address || '',
      marker_type: marker.marker_type || 'custom',
      property_type: marker.property_type || '',
      price_text: marker.price_text || '',
      price_value: marker.price_value || null,
      rent_price_text: marker.rent_price_text || '',
      rent_price_value: marker.rent_price_value || null,
      image_url: marker.image_url || '',
      description: marker.description || '',
      area_text: marker.area_text || '',
      bedrooms: marker.bedrooms || null,
      external_url: marker.external_url || '',
      latitude: marker.latitude,
      longitude: marker.longitude,
      marker_color: marker.marker_color || '#3B82F6',
      label_text: marker.label_text || '',
    })
    setShowMarkerForm(true)
    setSelectedMarker(marker)

    if (mapEditorRef.current) {
      mapEditorRef.current.panTo(marker.latitude, marker.longitude, 16)
    }
  }

  const handleDeleteMarker = async (markerId) => {
    if (!confirm('Bạn có chắc muốn xóa marker này?')) return

    try {
      await mapService.deleteMarker(mapId, markerId)
      toast.success('Đã xóa marker')
      setMarkers((prev) => prev.filter((m) => m.id !== markerId))
      if (selectedMarker?.id === markerId) {
        setSelectedMarker(null)
      }
    } catch (error) {
      console.error('Failed to delete marker:', error)
      toast.error('Không thể xóa marker')
    }
  }

  const resetMarkerForm = () => {
    setMarkerForm({
      name: '',
      address: '',
      marker_type: 'custom',
      property_type: '',
      price_text: '',
      price_value: null,
      rent_price_text: '',
      rent_price_value: null,
      image_url: '',
      description: '',
      area_text: '',
      bedrooms: null,
      external_url: '',
      latitude: null,
      longitude: null,
      marker_color: '#3B82F6',
      label_text: '',
    })
    setShowMarkerForm(false)
    setSelectedMarker(null)
    setSelectedTemplateId(null)
    setShowTemplates(true)
  }

  // Handle template selection
  const handleTemplateSelect = (template) => {
    setSelectedTemplateId(template.id)
    setMarkerForm((prev) => ({
      ...prev,
      name: template.name,
      marker_type: template.marker_type,
      property_type: template.property_type || '',
      marker_color: template.color,
      label_text: template.label_text || '',
    }))
    setShowTemplates(false)
  }

  const handlePublish = async () => {
    if (markers.length === 0) {
      toast.error('Vui lòng thêm ít nhất 1 marker trước khi xuất bản')
      return
    }

    try {
      const response = await mapService.publishMap(mapId)
      if (response.status === 'success') {
        toast.success('Map đã được xuất bản!')
        setMapData((prev) => ({
          ...prev,
          status: 'published',
          share_token: response.data.share_token,
        }))
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể xuất bản')
    }
  }

  const handleCopyShareLink = async () => {
    if (!mapData.share_token) return

    const url = mapService.getShareUrl(mapData.share_token)
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Đã copy link chia sẻ!')
    } catch {
      toast.error('Không thể copy link')
    }
  }

  const filteredMarkers = markers.filter((m) => {
    if (filterType === 'all') return true
    if (filterType === 'ban') return m.marker_type === 'ban' || m.marker_type === 'both'
    if (filterType === 'thue') return m.marker_type === 'thue' || m.marker_type === 'both'
    if (filterType === 'custom') return m.marker_type === 'custom'
    return true
  })

  // Mobile: Confirm location and get address
  const handleMobileLocationConfirm = useCallback(async () => {
    if (!pendingLocation) {
      // Get current center if pendingLocation is not set
      if (mapEditorRef.current) {
        const center = mapEditorRef.current.getCenter()
        if (center) {
          setPendingLocation({ lat: center.lat, lng: center.lng })
        }
      }
    }

    const location = pendingLocation || (mapEditorRef.current?.getCenter())
    if (!location) {
      toast.error('Không thể lấy vị trí')
      return
    }

    // Get address via reverse geocoding
    let address = ''
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.lat}&lon=${location.lng}&zoom=18&addressdetails=1`,
        { headers: { 'Accept-Language': 'vi' } }
      )
      const data = await response.json()
      if (data.display_name) {
        address = data.display_name
      }
    } catch (e) {
      console.error('Reverse geocoding failed:', e)
    }

    // Set marker form with location
    setMarkerForm((prev) => ({
      ...prev,
      latitude: location.lat,
      longitude: location.lng,
      address: address,
    }))

    // Exit adding mode and show marker modal
    setMobileAddingMarker(false)
    setPendingLocation(null)
    setShowMobileMarkerModal(true)
  }, [pendingLocation])

  // Mobile: Cancel adding marker
  const handleMobileCancelAdding = useCallback(() => {
    setMobileAddingMarker(false)
    setPendingLocation(null)
  }, [])

  // Mobile: Save marker from mobile modal
  const handleMobileSaveMarker = useCallback(async (formData) => {
    if (!formData.name.trim()) {
      toast.error('Vui lòng nhập tên marker')
      return
    }

    try {
      let response
      if (formData.id) {
        response = await mapService.updateMarker(mapId, formData.id, formData)
      } else {
        response = await mapService.addMarker(mapId, formData)
      }

      if (response.status === 'success') {
        toast.success(formData.id ? 'Đã cập nhật marker' : 'Đã thêm marker')

        if (formData.id) {
          setMarkers((prev) =>
            prev.map((m) => (m.id === formData.id ? response.data : m))
          )
        } else {
          setMarkers((prev) => [...prev, response.data])
        }

        setShowMobileMarkerModal(false)
        resetMarkerForm()
      }
    } catch (error) {
      console.error('Failed to save marker:', error)
      toast.error('Không thể lưu marker')
    }
  }, [mapId])

  // Mobile: Edit marker
  const handleMobileEditMarker = useCallback((marker) => {
    setMarkerForm({
      id: marker.id,
      name: marker.name || '',
      address: marker.address || '',
      marker_type: marker.marker_type || 'custom',
      property_type: marker.property_type || '',
      price_text: marker.price_text || '',
      price_value: marker.price_value || null,
      rent_price_text: marker.rent_price_text || '',
      rent_price_value: marker.rent_price_value || null,
      image_url: marker.image_url || '',
      description: marker.description || '',
      area_text: marker.area_text || '',
      bedrooms: marker.bedrooms || null,
      external_url: marker.external_url || '',
      latitude: marker.latitude,
      longitude: marker.longitude,
      marker_color: marker.marker_color || '#3B82F6',
      label_text: marker.label_text || '',
    })
    setShowMobileMarkerModal(true)

    // Pan to marker
    if (mapEditorRef.current) {
      mapEditorRef.current.panTo(marker.latitude, marker.longitude, 16)
    }
  }, [])

  // Loading state
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Đang tải map...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <Toaster position="top-center" />

      {/* Compact Header */}
      <header className="bg-white border-b border-gray-200 px-4 h-14 flex items-center justify-between z-20 shadow-sm">
        <div className="flex items-center gap-3">
          {/* Back button */}
          <button
            onClick={() => navigate('/maps')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <HiOutlineChevronLeft className="w-5 h-5 text-gray-600" />
          </button>

          {/* Logo */}
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
              <HiOutlineMap className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-gray-900">Map Editor</span>
          </div>

          {/* Divider */}
          <div className="hidden sm:block w-px h-6 bg-gray-200" />

          {/* Map name input */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={mapData.name}
              onChange={(e) => setMapData({ ...mapData, name: e.target.value })}
              placeholder="Tên map..."
              className="text-sm font-medium bg-transparent border-none focus:ring-0 p-0 w-40 sm:w-56 placeholder-gray-400"
            />
            {mapData.status === 'published' && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                Đã xuất bản
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Settings */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              showSettings ? 'bg-primary-100 text-primary-600' : 'hover:bg-gray-100 text-gray-600'
            )}
          >
            <HiOutlineCog className="w-5 h-5" />
          </button>

          {/* Published actions */}
          {mapData.status === 'published' && mapData.share_token && (
            <>
              <button
                onClick={() => window.open(`/map/view/${mapData.share_token}`, '_blank')}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <HiOutlineEye className="w-4 h-4" />
                Xem
              </button>
              <button
                onClick={handleCopyShareLink}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <HiOutlineDocumentDuplicate className="w-4 h-4" />
                Copy
              </button>
            </>
          )}

          {/* Publish button */}
          {mapData.status !== 'published' && isEditMode && (
            <button
              onClick={handlePublish}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
            >
              <HiOutlineGlobe className="w-4 h-4" />
              Xuất bản
            </button>
          )}

          {/* Save button */}
          <button
            onClick={handleSaveMap}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <HiOutlineSave className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Lưu</span>
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Hidden on mobile */}
        <aside
          className={clsx(
            'bg-white border-r border-gray-200 flex flex-col transition-all duration-300 z-10',
            'hidden md:flex', // Hide on mobile
            sidebarOpen ? 'w-80' : 'w-0'
          )}
        >
          {/* Search */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm địa điểm..."
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all"
              />
            </div>
          </div>

          {/* Filter tabs */}
          <div className="p-3 border-b border-gray-100">
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
              {[
                { value: 'all', label: 'Tất cả' },
                { value: 'custom', label: 'Tự do', color: '#10B981' },
                { value: 'ban', label: 'Bán', color: '#EF4444' },
                { value: 'thue', label: 'Thuê', color: '#3B82F6' },
              ].map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setFilterType(tab.value)}
                  className={clsx(
                    'flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all',
                    filterType === tab.value
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  {tab.color && (
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1"
                      style={{ backgroundColor: tab.color }}
                    />
                  )}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Add marker button */}
          <div className="p-3 border-b border-gray-100">
            {isEditMode ? (
              <div className="space-y-2">
                {isAddingMarker ? (
                  <button
                    onClick={() => setIsAddingMarker(false)}
                    className="w-full py-2.5 rounded-xl font-medium bg-primary-600 text-white shadow-lg shadow-primary-200 flex items-center justify-center gap-2"
                  >
                    <HiOutlineLocationMarker className="w-5 h-5 animate-pulse" />
                    Click vào bản đồ...
                  </button>
                ) : (
                  <button
                    onClick={() => setShowAddMarkerMode(true)}
                    className="w-full py-2.5 rounded-xl font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center justify-center gap-2 transition-all"
                  >
                    <HiOutlinePlus className="w-5 h-5" />
                    Thêm marker
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                Lưu map trước để thêm marker
              </div>
            )}
          </div>

          {/* Marker list */}
          <div className="flex-1 overflow-y-auto">
            {filteredMarkers.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <HiOutlineLocationMarker className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-sm text-gray-500">Chưa có marker nào</p>
                <p className="text-xs text-gray-400 mt-1">
                  Click vào bản đồ để thêm
                </p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredMarkers.map((marker) => (
                  <div
                    key={marker.id}
                    className={clsx(
                      'p-3 rounded-xl cursor-pointer transition-all group',
                      selectedMarker?.id === marker.id
                        ? 'bg-primary-50 ring-1 ring-primary-200'
                        : 'hover:bg-gray-50'
                    )}
                    onClick={() => {
                      setSelectedMarker(marker)
                      if (mapEditorRef.current) {
                        mapEditorRef.current.panTo(marker.latitude, marker.longitude, 16)
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Thumbnail */}
                      <div
                        className="w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center"
                        style={{ backgroundColor: `${getMarkerColor(marker)}15` }}
                      >
                        {marker.image_url ? (
                          <img
                            src={marker.image_url}
                            alt={marker.name}
                            className="w-full h-full object-cover rounded-lg"
                            onError={(e) => {
                              e.target.style.display = 'none'
                              e.target.parentElement.innerHTML = `<svg class="w-5 h-5" style="color: ${getMarkerColor(marker)}" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>`
                            }}
                          />
                        ) : (
                          <HiOutlineLocationMarker
                            className="w-5 h-5"
                            style={{ color: getMarkerColor(marker) }}
                          />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 text-sm truncate">
                          {marker.name}
                        </h4>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: getMarkerColor(marker) }}
                          />
                          <span className="text-xs text-gray-500">
                            {markerTypeOptions.find((o) => o.value === marker.marker_type)?.label}
                          </span>
                        </div>
                        {/* Label or Price */}
                        {marker.marker_type === 'custom' && marker.label_text && (
                          <p
                            className="text-sm font-semibold mt-1 truncate"
                            style={{ color: getMarkerColor(marker) }}
                          >
                            {marker.label_text}
                          </p>
                        )}
                        {marker.marker_type !== 'custom' && marker.price_text && (
                          <p className="text-sm font-semibold text-red-600 mt-1">{marker.price_text}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditMarker(marker)
                          }}
                          className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <HiOutlinePencil className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteMarker(marker.id)
                          }}
                          className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <HiOutlineTrash className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer stats */}
          <div className="p-3 border-t border-gray-100 bg-gray-50">
            <div className="flex justify-between text-xs text-gray-500">
              <span>Tổng: {markers.length} markers</span>
              <span>Hiển thị: {filteredMarkers.length}</span>
            </div>
          </div>
        </aside>

        {/* Map container */}
        <div className="flex-1 relative">
          <AdvancedMapEditor
            ref={mapEditorRef}
            mapId={mapId ? parseInt(mapId) : null}
            markers={markers}
            center={{ lat: mapData.center_lat, lng: mapData.center_lng }}
            zoom={mapData.zoom_level}
            mapStyle={mapData.map_style}
            showCenterMarker={false}
            onCenterChange={handleCenterChange}
            onZoomChange={handleZoomChange}
            onMarkerClick={handleMarkerClick}
            onTempMarkerConfirm={handleTempMarkerConfirm}
            onMoveEnd={handleMapMoveEnd}
            addingMarker={isAddingMarker}
            mapType="property"
            distanceLine={distanceLine}
          />

          {/* Sidebar toggle (desktop only) */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="absolute top-4 left-4 z-10 p-2.5 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all hidden md:block"
          >
            <HiOutlineMenuAlt2 className="w-5 h-5 text-gray-700" />
          </button>

          {/* Adding marker instruction - Desktop only */}
          {isAddingMarker && !isMobile && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-primary-600 text-white px-4 py-2.5 rounded-full shadow-lg text-sm font-medium flex items-center gap-2 animate-pulse">
              <HiOutlineLocationMarker className="w-4 h-4" />
              Click vào bản đồ để đặt marker
              <button
                onClick={() => setIsAddingMarker(false)}
                className="ml-1 hover:bg-primary-700 rounded-full p-1"
              >
                <HiOutlineX className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Legend - hidden on mobile */}
          <div className="absolute bottom-4 right-4 bg-white rounded-xl shadow-lg p-3 hidden md:block">
            <div className="text-xs font-medium text-gray-700 mb-2">Chú thích</div>
            <div className="space-y-1.5">
              {markerTypeOptions.map((opt) => (
                <div key={opt.value} className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: opt.color }}
                  />
                  <span className="text-xs text-gray-600">{opt.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Settings Panel - hidden on mobile, will use modal instead */}
        {showSettings && (
          <aside className="w-80 bg-white border-l border-gray-200 overflow-y-auto hidden md:block">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Cài đặt Map</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Map Style Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kiểu bản đồ
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {MAP_STYLE_OPTIONS.map((style) => (
                    <button
                      key={style.value}
                      onClick={() => {
                        setMapData({ ...mapData, map_style: style.value })
                        if (mapEditorRef.current?.setMapStyle) {
                          mapEditorRef.current.setMapStyle(style.value)
                        }
                      }}
                      className={clsx(
                        'px-3 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2',
                        mapData.map_style === style.value
                          ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      <span>{style.icon}</span>
                      <span>{style.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mô tả
                </label>
                <textarea
                  value={mapData.description || ''}
                  onChange={(e) => setMapData({ ...mapData, description: e.target.value })}
                  placeholder="Mô tả ngắn về map này..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Thumbnail */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ảnh đại diện (URL)
                </label>
                <input
                  type="text"
                  value={mapData.thumbnail_url || ''}
                  onChange={(e) => setMapData({ ...mapData, thumbnail_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {mapData.thumbnail_url && (
                  <img
                    src={mapData.thumbnail_url}
                    alt="Thumbnail"
                    className="mt-2 w-full h-20 object-cover rounded-lg"
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                )}
              </div>

              {/* Center & Zoom Settings */}
              <div className="pt-3 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vị trí mặc định
                </label>
                <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Latitude:</span>
                      <span className="ml-1 font-mono text-gray-700">{mapData.center_lat?.toFixed(6)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Longitude:</span>
                      <span className="ml-1 font-mono text-gray-700">{mapData.center_lng?.toFixed(6)}</span>
                    </div>
                  </div>
                  <div className="text-xs">
                    <span className="text-gray-500">Zoom:</span>
                    <span className="ml-1 font-mono text-gray-700">{mapData.zoom_level}</span>
                  </div>
                  <button
                    onClick={() => {
                      if (mapEditorRef.current) {
                        const currentCenter = mapEditorRef.current.getCenter()
                        const currentZoom = mapEditorRef.current.getZoom()
                        if (currentCenter && currentZoom) {
                          setMapData(prev => ({
                            ...prev,
                            center_lat: currentCenter.lat,
                            center_lng: currentCenter.lng,
                            zoom_level: currentZoom,
                          }))
                          toast.success('Đã cập nhật vị trí mặc định')
                        }
                      }
                    }}
                    className="w-full mt-2 py-2 px-3 text-xs font-medium text-primary-700 bg-primary-100 hover:bg-primary-200 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                  >
                    <HiOutlineCursorClick className="w-4 h-4" />
                    Đặt vị trí hiện tại làm mặc định
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Di chuyển bản đồ đến vị trí mong muốn, sau đó nhấn nút trên để lưu làm mặc định khi mở map.
                </p>
              </div>
            </div>
          </aside>
        )}
      </div>

      {/* Marker Form Modal - Desktop only */}
      {showMarkerForm && !isMobile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {markerForm.id ? 'Sửa marker' : 'Thêm marker mới'}
              </h3>
              <button
                onClick={resetMarkerForm}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Templates (only for new markers) */}
              {!markerForm.id && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Chọn mẫu có sẵn
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowTemplates(!showTemplates)}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      {showTemplates ? 'Ẩn đi' : 'Hiển mẫu'}
                    </button>
                  </div>
                  {showTemplates && (
                    <div className="bg-gray-50 rounded-xl p-3 max-h-48 overflow-y-auto">
                      <MarkerTemplates
                        onSelect={handleTemplateSelect}
                        selectedId={selectedTemplateId}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên điểm đánh dấu *
                </label>
                <input
                  type="text"
                  value={markerForm.name}
                  onChange={(e) => setMarkerForm({ ...markerForm, name: e.target.value })}
                  placeholder="VD: The Maison, Can ho Monarchy..."
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Địa chỉ
                </label>
                <input
                  type="text"
                  value={markerForm.address}
                  onChange={(e) => setMarkerForm({ ...markerForm, address: e.target.value })}
                  placeholder="Số nhà, đường, phường..."
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Marker type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loại marker
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {markerTypeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setMarkerForm({ ...markerForm, marker_type: opt.value })}
                      className={clsx(
                        'px-3 py-2 rounded-xl text-xs font-medium transition-all flex flex-col items-center gap-1',
                        markerForm.marker_type === opt.value
                          ? 'ring-2 ring-offset-1 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                      style={{
                        backgroundColor: markerForm.marker_type === opt.value ? opt.color : undefined,
                        ringColor: markerForm.marker_type === opt.value ? opt.color : undefined,
                      }}
                    >
                      <span>{opt.icon}</span>
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom marker fields */}
              {markerForm.marker_type === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nhãn hiển thị
                    </label>
                    <input
                      type="text"
                      value={markerForm.label_text}
                      onChange={(e) => setMarkerForm({ ...markerForm, label_text: e.target.value })}
                      placeholder="VD: 50tr/m², Dự án hot, Còn 2 căn..."
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Màu marker
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                      {colorPresets.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => setMarkerForm({ ...markerForm, marker_color: color.value })}
                          className={clsx(
                            'w-8 h-8 rounded-lg transition-all relative',
                            markerForm.marker_color === color.value && 'ring-2 ring-offset-2 ring-gray-400'
                          )}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        >
                          {markerForm.marker_color === color.value && (
                            <HiOutlineCheck className="w-4 h-4 text-white absolute inset-0 m-auto" />
                          )}
                        </button>
                      ))}
                      <input
                        type="color"
                        value={markerForm.marker_color}
                        onChange={(e) => setMarkerForm({ ...markerForm, marker_color: e.target.value })}
                        className="w-8 h-8 rounded-lg cursor-pointer border-2 border-gray-200"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Property type and price fields */}
              {markerForm.marker_type !== 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Loại BĐS
                    </label>
                    <select
                      value={markerForm.property_type}
                      onChange={(e) => setMarkerForm({ ...markerForm, property_type: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Chọn loại</option>
                      {propertyTypeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {(markerForm.marker_type === 'ban' || markerForm.marker_type === 'both') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Giá bán
                        </label>
                        <input
                          type="text"
                          value={markerForm.price_text}
                          onChange={(e) => setMarkerForm({ ...markerForm, price_text: e.target.value })}
                          placeholder="VD: 2.5 tỷ"
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    )}
                    {(markerForm.marker_type === 'thue' || markerForm.marker_type === 'both') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Giá thuê
                        </label>
                        <input
                          type="text"
                          value={markerForm.rent_price_text}
                          onChange={(e) => setMarkerForm({ ...markerForm, rent_price_text: e.target.value })}
                          placeholder="VD: 15tr/tháng"
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hình ảnh (URL)
                </label>
                <input
                  type="text"
                  value={markerForm.image_url}
                  onChange={(e) => setMarkerForm({ ...markerForm, image_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {markerForm.image_url && (
                  <img
                    src={markerForm.image_url}
                    alt="Preview"
                    className="mt-2 w-full h-24 object-cover rounded-xl"
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mô tả
                </label>
                <textarea
                  value={markerForm.description}
                  onChange={(e) => setMarkerForm({ ...markerForm, description: e.target.value })}
                  placeholder="Thông tin chi tiết..."
                  rows={2}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Coordinates (readonly) */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Latitude</label>
                  <input
                    type="text"
                    value={markerForm.latitude || ''}
                    readOnly
                    className="w-full px-3 py-2 text-xs bg-gray-50 border-0 rounded-lg font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Longitude</label>
                  <input
                    type="text"
                    value={markerForm.longitude || ''}
                    readOnly
                    className="w-full px-3 py-2 text-xs bg-gray-50 border-0 rounded-lg font-mono"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={resetMarkerForm}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={handleSaveMarker}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors"
              >
                {markerForm.id ? 'Cập nhật' : 'Thêm marker'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Marker Mode Selection Modal - Desktop only */}
      {showAddMarkerMode && !isMobile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Thêm marker mới</h3>
              <button
                onClick={() => {
                  setShowAddMarkerMode(false)
                  setManualCoords({ lat: '', lng: '' })
                }}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">Chọn cách thêm marker:</p>

              {/* Option 1: Click on map */}
              <button
                onClick={() => {
                  setShowAddMarkerMode(false)
                  setIsAddingMarker(true)
                }}
                className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-primary-500 hover:bg-primary-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <HiOutlineCursorClick className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Click trên bản đồ</h4>
                    <p className="text-sm text-gray-500">Kéo thả marker đến vị trí mong muốn</p>
                  </div>
                </div>
              </button>

              {/* Option 2: Manual coordinates */}
              <div className="p-4 rounded-xl border-2 border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <HiOutlineLocationMarker className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Nhập tọa độ</h4>
                    <p className="text-sm text-gray-500">Nhập chính xác latitude, longitude</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      value={manualCoords.lat}
                      onChange={(e) => setManualCoords({ ...manualCoords, lat: e.target.value })}
                      placeholder="VD: 16.0544"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      value={manualCoords.lng}
                      onChange={(e) => setManualCoords({ ...manualCoords, lng: e.target.value })}
                      placeholder="VD: 108.2022"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                    />
                  </div>
                </div>

                <button
                  onClick={async () => {
                    const lat = parseFloat(manualCoords.lat)
                    const lng = parseFloat(manualCoords.lng)
                    if (isNaN(lat) || isNaN(lng)) {
                      toast.error('Vui lòng nhập tọa độ hợp lệ')
                      return
                    }
                    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                      toast.error('Tọa độ không hợp lệ')
                      return
                    }

                    // Get address from coordinates
                    let address = ''
                    try {
                      const response = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
                        { headers: { 'Accept-Language': 'vi' } }
                      )
                      const data = await response.json()
                      if (data.display_name) {
                        address = data.display_name
                      }
                    } catch (e) {
                      console.error('Reverse geocoding failed:', e)
                    }

                    setMarkerForm(prev => ({
                      ...prev,
                      latitude: lat,
                      longitude: lng,
                      address: address,
                    }))
                    setShowAddMarkerMode(false)
                    setShowMarkerForm(true)
                    setManualCoords({ lat: '', lng: '' })

                    // Pan map to the coordinates
                    if (mapEditorRef.current) {
                      mapEditorRef.current.panTo(lat, lng, 16)
                    }
                  }}
                  disabled={!manualCoords.lat || !manualCoords.lng}
                  className="w-full mt-3 py-2 px-3 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Xác nhận tọa độ
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Tip: Copy tọa độ từ Google Maps bằng cách click phải vào vị trí
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create Map Confirmation Modal */}
      {showCreateMapModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-in zoom-in-95">
            <div className="p-5 text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <HiOutlineMap className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Tạo bản đồ mới
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Bạn muốn tạo bản đồ với tên:
              </p>
              <div className="bg-gray-50 rounded-xl px-4 py-3 mb-5">
                <p className="font-medium text-gray-900 truncate">{tempMapName}</p>
              </div>
            </div>

            <div className="px-5 pb-5 space-y-2">
              <button
                onClick={() => handleConfirmCreateMap(tempMapName)}
                disabled={saving}
                className="w-full py-3 rounded-xl font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <HiOutlineCheck className="w-5 h-5" />
                )}
                Đồng ý
              </button>
              <button
                onClick={() => {
                  setShowCreateMapModal(false)
                  setShowRenameModal(true)
                }}
                className="w-full py-3 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                <HiOutlinePencil className="w-5 h-5" />
                Đổi tên
              </button>
              <button
                onClick={() => navigate('/maps')}
                className="w-full py-3 rounded-xl font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Map Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-in zoom-in-95">
            <div className="p-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                Đặt tên bản đồ
              </h3>
              <input
                type="text"
                value={tempMapName}
                onChange={(e) => setTempMapName(e.target.value)}
                placeholder="Nhập tên bản đồ..."
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-4"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tempMapName.trim()) {
                    handleConfirmCreateMap(tempMapName)
                  }
                }}
              />
            </div>

            <div className="px-5 pb-5 flex gap-3">
              <button
                onClick={() => {
                  setShowRenameModal(false)
                  setShowCreateMapModal(true)
                }}
                className="flex-1 py-3 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Quay lại
              </button>
              <button
                onClick={() => handleConfirmCreateMap(tempMapName)}
                disabled={!tempMapName.trim() || saving}
                className="flex-1 py-3 rounded-xl font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Đang tạo...' : 'Xác nhận'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Components */}
      {isMobile && (
        <>
          {/* Mobile Bottom Sheet - Marker List */}
          {!mobileAddingMarker && !showCreateMapModal && !showRenameModal && (
            <MobileMapBottomSheet
              markers={markers}
              selectedMarker={selectedMarker}
              onMarkerSelect={(marker) => {
                setSelectedMarker(marker)
                if (mapEditorRef.current) {
                  mapEditorRef.current.panTo(marker.latitude, marker.longitude, 16)
                }
              }}
              onMarkerEdit={handleMobileEditMarker}
              onMarkerDelete={handleDeleteMarker}
              filterType={filterType}
              onFilterChange={setFilterType}
              getMarkerColor={getMarkerColor}
            />
          )}

          {/* Mobile FAB */}
          {isEditMode && !mobileAddingMarker && !showCreateMapModal && !showRenameModal && (
            <MobileMapFAB
              onAddMarker={() => setMobileAddingMarker(true)}
              onMyLocation={() => {
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      const { latitude, longitude } = position.coords
                      if (mapEditorRef.current) {
                        mapEditorRef.current.panTo(latitude, longitude, 16)
                      }
                      toast.success('Đã di chuyển đến vị trí của bạn')
                    },
                    () => toast.error('Không thể lấy vị trí hiện tại')
                  )
                }
              }}
              isAddingMarker={mobileAddingMarker}
            />
          )}

          {/* Mobile Location Confirm - Pin in center */}
          <MobileLocationConfirm
            isActive={mobileAddingMarker}
            onConfirm={handleMobileLocationConfirm}
            onCancel={handleMobileCancelAdding}
            currentAddress={pendingLocation ? `${pendingLocation.lat.toFixed(6)}, ${pendingLocation.lng.toFixed(6)}` : 'Kéo bản đồ để chọn vị trí'}
          />

          {/* Mobile Marker Modal */}
          <MobileMarkerModal
            isOpen={showMobileMarkerModal}
            onClose={() => {
              setShowMobileMarkerModal(false)
              resetMarkerForm()
            }}
            onSave={handleMobileSaveMarker}
            initialData={markerForm}
            isEdit={!!markerForm.id}
          />
        </>
      )}
    </div>
  )
}

export default MapEditor
